import { useApolloClient } from "@apollo/client";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { PROGRAM_FORM_MODE } from "features/LoyaltyPackage/core/constants";
import {
  prependProgramToListCache,
  refreshProgramDetailFromNetwork,
  updateProgramInListCache,
  useCreateLoyaltyProgram,
  useUpdateLoyaltyProgram,
} from "features/LoyaltyPackage/data/hooks";
import {
  buildProgramInput,
  mapProgramToFormValues,
  validateProgramDateRange,
} from "features/LoyaltyPackage/core/utils";
import { notify } from "utils/helperFuncs";

const INITIAL_MODAL_STATE = {
  step: null,
  mode: PROGRAM_FORM_MODE.CREATE,
  editingRecord: null,
  formValues: null,
};

export default function useProgramFormModal({
  refetch,
  listRefetch,
  programId,
  afterSubmitRefetch = "list",
  listQueryVariables,
  onClose,
} = {}) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [modalState, setModalState] = useState(INITIAL_MODAL_STATE);

  const { create, loading: createLoading } = useCreateLoyaltyProgram();
  const { update, loading: updateLoading } = useUpdateLoyaltyProgram();

  const isSubmitting = createLoading || updateLoading;
  const isFormOpen = modalState.step === "form";
  const isConfirmOpen = modalState.step === "confirm";
  const isEditMode = modalState.mode === PROGRAM_FORM_MODE.EDIT;
  const formSessionKey = isFormOpen ? `${modalState.mode}-${modalState.editingRecord?.id ?? "new"}` : "closed";

  const closeAll = useCallback(() => {
    setModalState(INITIAL_MODAL_STATE);
    onClose?.();
  }, [onClose]);

  const openCreate = useCallback(() => {
    setModalState({
      step: "form",
      mode: PROGRAM_FORM_MODE.CREATE,
      editingRecord: null,
      formValues: null,
    });
  }, []);

  const openEdit = useCallback((record) => {
    setModalState({
      step: "form",
      mode: PROGRAM_FORM_MODE.EDIT,
      editingRecord: record,
      formValues: null,
    });
  }, []);

  const openCopy = useCallback((record) => {
    setModalState({
      step: "form",
      mode: PROGRAM_FORM_MODE.COPY,
      editingRecord: record,
      formValues: null,
    });
  }, []);

  const submitForm = useCallback(async (values) => {
    const dateValidation = validateProgramDateRange(values.dateRange);
    if (!dateValidation.valid) {
      notify.error({ message: dateValidation.message });
      return;
    }

    setModalState((prev) => ({
      ...prev,
      step: "confirm",
      formValues: values,
    }));
  }, []);

  const backToForm = useCallback(() => {
    setModalState((prev) => ({ ...prev, step: "form" }));
  }, []);

  const confirmSubmit = useCallback(async () => {
    if (!modalState.formValues) return;

    const input = buildProgramInput(modalState.formValues);

    try {
      const editingId = modalState.editingRecord?.id;
      let savedProgram;

      if (isEditMode) {
        savedProgram = await update(editingId, input);
        notify.success({ message: t("loyaltyPackage.program.updateSuccess") });
      } else {
        savedProgram = await create(input);
        notify.success({ message: t("loyaltyPackage.program.createSuccess") });
      }

      closeAll();

      if (afterSubmitRefetch === "detail" && programId) {
        const freshProgram = await refreshProgramDetailFromNetwork(client, programId);
        // Đồng bộ list cache nếu user đã xem tab list trước đó — không gọi network
        updateProgramInListCache(client, programId, freshProgram ?? savedProgram, undefined, {
          offset: 0,
          limit: 10,
        });
      } else if (afterSubmitRefetch === "list" && listQueryVariables) {
        if (isEditMode) {
          const patched = updateProgramInListCache(
            client,
            editingId,
            savedProgram,
            listQueryVariables.filters,
            listQueryVariables.pagination
          );
          if (!patched) await listRefetch?.();
        } else {
          const prepended = prependProgramToListCache(
            client,
            savedProgram,
            listQueryVariables.filters,
            listQueryVariables.pagination
          );
          if (!prepended) await listRefetch?.();
        }
      } else {
        await refetch?.();
      }
    } catch (err) {
      notify.error({
        message:
          err?.message ||
          (isEditMode ? t("loyaltyPackage.program.updateFailed") : t("loyaltyPackage.program.createFailed")),
      });
    }
  }, [
    modalState,
    isEditMode,
    create,
    update,
    closeAll,
    refetch,
    listRefetch,
    afterSubmitRefetch,
    programId,
    listQueryVariables,
    client,
    t,
  ]);

  const getInitialFormValues = useCallback(() => {
    if (modalState.mode === PROGRAM_FORM_MODE.CREATE) {
      return {};
    }

    if (modalState.mode === PROGRAM_FORM_MODE.COPY) {
      return mapProgramToFormValues(modalState.editingRecord, { clearDates: true });
    }

    return mapProgramToFormValues(modalState.editingRecord);
  }, [modalState.mode, modalState.editingRecord]);

  return {
    modalState,
    isFormOpen,
    isConfirmOpen,
    isEditMode,
    isSubmitting,
    formSessionKey,
    openCreate,
    openEdit,
    openCopy,
    closeAll,
    submitForm,
    backToForm,
    confirmSubmit,
    getInitialFormValues,
    pendingFormValues: modalState.formValues,
    editingRecord: modalState.editingRecord,
    formMode: modalState.mode,
  };
}
